import { backgroundStops, primaryColor } from './eventBackground';

// Draws a shareable event card onto a canvas, styled like a collectible
// trading card (name bar, level stars, framed art, text box, set code).
// Uses only the Canvas 2D API so no extra dependencies are needed.

export const SHARE_FORMATS = {
  story: { label: 'Story 9:16', width: 1080, height: 1920 },
  post: { label: 'Post 4:5', width: 1080, height: 1350 },
};

// Frolik's interface font (index.css forces Arial/Helvetica site-wide).
const SANS = 'Arial, Helvetica, sans-serif';

// Card body: orange gradient matching the site background and shiny outlines.
const CARD_BACKGROUND = 'linear-gradient(135deg, #F8D3B5 0%, #FF8F34 48%, #F56E00 100%)';

// Real trading cards are 59 x 86 mm.
const CARD_RATIO = 86 / 59;

export const RARITIES = ['Common', 'Rare', 'Super Rare', 'Ultra Rare'];

// Splits text into lines that fit maxWidth, truncating with "…" after maxLines.
export function wrapText(measure, text, maxWidth, maxLines) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let current = '';

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (measure(candidate) <= maxWidth || !current) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);

  if (lines.length <= maxLines) return lines;

  const kept = lines.slice(0, maxLines);
  let last = kept[maxLines - 1];
  while (last.length > 1 && measure(`${last}…`) > maxWidth) {
    last = last.slice(0, -1).trimEnd();
  }
  kept[maxLines - 1] = `${last}…`;
  return kept;
}

export function displayUrl(url) {
  return String(url || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
}

// True when dark text reads better than white on this colour.
export function isLightColor(color) {
  const hex = String(color || '').trim().replace(/^#/, '');
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return false;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255);
  // Relative luminance (sRGB approximation).
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.6;
}

// Darkens (amount < 0) or lightens (amount > 0) a hex colour by a fraction.
export function shadeColor(color, amount) {
  const hex = String(color || '').trim().replace(/^#/, '');
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return color;
  const channels = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
  const target = amount < 0 ? 0 : 255;
  const mixed = channels.map((value) => Math.round(value + (target - value) * Math.abs(amount)));
  return `#${mixed.map((value) => value.toString(16).padStart(2, '0')).join('')}`;
}

// Same event, same card: level and rarity come from a hash of its slug.
export function cardStats(slug) {
  let hash = 0;
  for (const character of String(slug || 'frolik')) {
    hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  }
  const roll = hash % 100;
  const rarity = roll < 55 ? 'Common' : roll < 80 ? 'Rare' : roll < 95 ? 'Super Rare' : 'Ultra Rare';
  const level = 3 + (Math.floor(hash / 100) % 6);
  return { level, rarity };
}

function roundedRect(ctx, x, y, width, height, radius) {
  // Square corners for now, like the rest of the site.
  radius = 0; // eslint-disable-line no-param-reassign
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
}

// Draws `image` to fill the box, cropping the overflow (like object-fit: cover).
function drawCover(ctx, image, x, y, width, height) {
  const scale = Math.max(width / image.width, height / image.height);
  const drawWidth = image.width * scale;
  const drawHeight = image.height * scale;
  ctx.drawImage(image, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight);
}

// Returns a copy of a (single-colour, transparent) logo recoloured to `color`.
function tintLogo(logo, color) {
  const tinted = document.createElement('canvas');
  tinted.width = logo.width;
  tinted.height = logo.height;
  const tctx = tinted.getContext('2d');
  if (!tctx) return logo;
  tctx.drawImage(logo, 0, 0);
  tctx.globalCompositeOperation = 'source-in';
  tctx.fillStyle = color;
  tctx.fillRect(0, 0, tinted.width, tinted.height);
  return tinted;
}

// Fills the canvas (or a box) with an event background: gradient or solid.
function fillBackground(ctx, value, x, y, width, height) {
  const stops = backgroundStops(value);
  if (stops.length > 1) {
    // 135deg in CSS runs from the top-left corner to the bottom-right.
    const gradient = ctx.createLinearGradient(x, y, x + width, y + height);
    stops.forEach((stop) => gradient.addColorStop(stop.at, stop.color));
    ctx.fillStyle = gradient;
  } else {
    ctx.fillStyle = primaryColor(value);
  }
  ctx.fillRect(x, y, width, height);
}

// Shiny orange-white-orange gradient for outlines. It spans the whole card so
// the white highlight sweeps diagonally across every outline, like foil.
function shineGradient(ctx, x, y, width, height) {
  const gradient = ctx.createLinearGradient(x, y, x + width, y + height);
  gradient.addColorStop(0, '#9b551b');
  gradient.addColorStop(0.3, '#ff8b2b');
  gradient.addColorStop(0.5, '#ffffff');
  gradient.addColorStop(0.7, '#ff8b2b');
  gradient.addColorStop(1, '#9b551b');
  return gradient;
}

// A framed panel: shiny outline with a thin light inner edge, like a card bevel.
function bevelBox(ctx, x, y, width, height, fill, unit, outline = '#1b1712') {
  ctx.fillStyle = outline;
  ctx.fillRect(x - unit * 0.45, y - unit * 0.45, width + unit * 0.9, height + unit * 0.9);
  ctx.fillStyle = fill;
  ctx.fillRect(x, y, width, height);
  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  ctx.lineWidth = Math.max(1, unit * 0.25);
  ctx.strokeRect(x + unit * 0.4, y + unit * 0.4, width - unit * 0.8, height - unit * 0.8);
}

function foilGradient(ctx, x, width, rarity) {
  const gradient = ctx.createLinearGradient(x, 0, x + width, 0);
  const stops = rarity === 'Ultra Rare'
    ? ['#7a5a12', '#f6e39a', '#b8902c', '#fff4c4', '#8d6b1c']
    : ['#5f6368', '#f4f6f8', '#9aa0a6', '#ffffff', '#6b7075'];
  stops.forEach((color, index) => gradient.addColorStop(index / (stops.length - 1), color));
  return gradient;
}

// Rainbow sheen over the art for holographic rarities.
function drawHolo(ctx, x, y, width, height) {
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, width, height);
  ctx.clip();
  // Multiply tints light areas too, so white art still looks holographic.
  ctx.globalCompositeOperation = 'multiply';
  ctx.globalAlpha = 0.32;
  const gradient = ctx.createLinearGradient(x, y, x + width, y + height);
  ['#ff4d4d', '#ffd84d', '#5dff7a', '#4dd2ff', '#ff8929', '#ff4de1', '#ff4d4d']
    .forEach((color, index, list) => gradient.addColorStop(index / (list.length - 1), color));
  ctx.fillStyle = gradient;
  ctx.fillRect(x, y, width, height);
  ctx.globalAlpha = 0.3;
  ctx.globalCompositeOperation = 'screen';
  for (let offset = -height; offset < width; offset += width / 6) {
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.beginPath();
    ctx.moveTo(x + offset, y);
    ctx.lineTo(x + offset + width / 14, y);
    ctx.lineTo(x + offset + width / 14 + height, y + height);
    ctx.lineTo(x + offset + height, y + height);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

// Picks the largest font size (down to minSize) at which text fits on one line.
function fitFont(ctx, text, maxWidth, startSize, minSize, font) {
  let size = startSize;
  ctx.font = font(size);
  while (size > minSize && ctx.measureText(text).width > maxWidth) {
    size -= 2;
    ctx.font = font(size);
  }
  const [line] = wrapText((value) => ctx.measureText(value).width, text, maxWidth, 1);
  return { size, line };
}

function drawCard(ctx, details, x, y, width, banner, logo) {
  const height = width * CARD_RATIO;
  const u = width / 100; // layout unit: 1% of card width
  const onFrame = isLightColor(primaryColor(CARD_BACKGROUND)) ? '#1b1712' : '#ffffff';
  const { level, rarity } = cardStats(details.slug);
  const shine = shineGradient(ctx, x, y, width, height);

  // Orange glow spreading from under the card.
  ctx.save();
  ctx.translate(x + width / 2, y + height * 0.92);
  ctx.scale(1, 0.32);
  const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, width * 0.75);
  glow.addColorStop(0, 'rgba(255, 184, 125, 0.85)');
  glow.addColorStop(0.45, 'rgba(255, 136, 38, 0.4)');
  glow.addColorStop(1, 'rgba(255, 136, 38, 0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(0, 0, width * 0.75, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Outer border and frame, with a orange halo.
  ctx.save();
  ctx.shadowColor = 'rgba(255, 136, 38, 0.8)';
  ctx.shadowBlur = 9 * u;
  ctx.shadowOffsetY = 3 * u;
  roundedRect(ctx, x, y, width, height, 1.5 * u);
  ctx.fillStyle = shine;
  ctx.fill();
  ctx.restore();

  const inset = 1.2 * u;
  ctx.save();
  roundedRect(ctx, x + inset, y + inset, width - inset * 2, height - inset * 2, 1 * u);
  ctx.clip();
  fillBackground(ctx, CARD_BACKGROUND, x + inset, y + inset, width - inset * 2, height - inset * 2);
  ctx.restore();

  const pad = 6 * u;
  const innerX = x + pad;
  const innerWidth = width - pad * 2;

  // Name bar with the frog as the "attribute" emblem.
  const nameY = y + pad;
  const nameHeight = 11 * u;
  bevelBox(ctx, innerX, nameY, innerWidth, nameHeight, '#ffffff', u, shine);
  const emblemRadius = 4.2 * u;
  const emblemX = innerX + innerWidth - emblemRadius - 1.6 * u;
  const emblemY = nameY + nameHeight / 2;
  const emblem = ctx.createRadialGradient(emblemX - u, emblemY - u, u, emblemX, emblemY, emblemRadius);
  emblem.addColorStop(0, '#ff8928');
  emblem.addColorStop(1, '#7b4519');
  ctx.beginPath();
  ctx.arc(emblemX, emblemY, emblemRadius, 0, Math.PI * 2);
  ctx.fillStyle = emblem;
  ctx.fill();
  ctx.lineWidth = 0.5 * u;
  ctx.strokeStyle = '#f2e6b8';
  ctx.stroke();
  if (logo) {
    const size = emblemRadius * 1.35;
    ctx.drawImage(tintLogo(logo, '#ffffff'), emblemX - size / 2, emblemY - size / 2, size, size);
  }

  const titleMax = innerWidth - emblemRadius * 2 - 6 * u;
  const { size: titleSize, line: titleLine } = fitFont(
    ctx,
    details.title || 'Untitled event',
    titleMax,
    7.6 * u,
    4.6 * u,
    (size) => `bold ${size}px ${SANS}`
  );
  ctx.textBaseline = 'middle';
  ctx.fillStyle = rarity === 'Common' ? '#1b1712' : foilGradient(ctx, innerX, titleMax, rarity);
  ctx.fillText(titleLine, innerX + 2.4 * u, nameY + nameHeight / 2 + titleSize * 0.05);
  if (rarity !== 'Common') {
    ctx.lineWidth = Math.max(1, 0.18 * u);
    ctx.strokeStyle = 'rgba(40,30,10,0.55)';
    ctx.strokeText(titleLine, innerX + 2.4 * u, nameY + nameHeight / 2 + titleSize * 0.05);
  }

  // Level stars, right-aligned under the name bar.
  const starRadius = 2.7 * u;
  const starsY = nameY + nameHeight + 4.4 * u;
  for (let index = 0; index < level; index += 1) {
    const cx = innerX + innerWidth - starRadius - 1.5 * u - index * (starRadius * 2 + 0.9 * u);
    const star = ctx.createRadialGradient(cx - u * 0.6, starsY - u * 0.6, u * 0.3, cx, starsY, starRadius);
    star.addColorStop(0, '#ff8a5c');
    star.addColorStop(1, '#a3200f');
    ctx.beginPath();
    ctx.arc(cx, starsY, starRadius, 0, Math.PI * 2);
    ctx.fillStyle = star;
    ctx.fill();
    ctx.lineWidth = 0.35 * u;
    ctx.strokeStyle = '#3a0c05';
    ctx.stroke();
    ctx.fillStyle = '#fff3c4';
    ctx.font = `bold ${starRadius * 1.5}px ${SANS}`;
    const glyph = '✶';
    ctx.fillText(glyph, cx - ctx.measureText(glyph).width / 2, starsY + u * 0.2);
  }

  // Artwork.
  const artInset = 4 * u;
  const artX = innerX + artInset;
  const artWidth = innerWidth - artInset * 2;
  const artY = starsY + starRadius + 2.4 * u;
  const artHeight = artWidth * 0.76;
  bevelBox(ctx, artX, artY, artWidth, artHeight, primaryColor(details.backgroundColor), u, shine);
  ctx.save();
  ctx.beginPath();
  ctx.rect(artX, artY, artWidth, artHeight);
  ctx.clip();
  fillBackground(ctx, details.backgroundColor, artX, artY, artWidth, artHeight);
  if (banner) {
    drawCover(ctx, banner, artX, artY, artWidth, artHeight);
  } else if (logo) {
    const size = artWidth * 0.62;
    const tint = isLightColor(primaryColor(details.backgroundColor)) ? '#1b1712' : '#ffffff';
    ctx.globalAlpha = 0.9;
    ctx.drawImage(tintLogo(logo, tint), artX + (artWidth - size) / 2, artY + (artHeight - size) / 2, size, size);
    ctx.globalAlpha = 1;
  }
  ctx.restore();
  if (rarity === 'Super Rare' || rarity === 'Ultra Rare') {
    drawHolo(ctx, artX, artY, artWidth, artHeight);
  }
  ctx.lineWidth = 0.5 * u;
  ctx.strokeStyle = shine;
  ctx.strokeRect(artX, artY, artWidth, artHeight);

  // Text box (white), straight under the art.
  const boxY = artY + artHeight + 2.6 * u;
  const boxBottom = y + height - pad - 5.6 * u;
  const boxHeight = boxBottom - boxY;
  bevelBox(ctx, innerX, boxY, innerWidth, boxHeight, '#ffffff', u, shine);
  const textX = innerX + 2.6 * u;
  const textWidth = innerWidth - 5.2 * u;
  const bodySize = 4.4 * u;
  const lineHeight = bodySize * 1.25;
  // Lines are drawn centred on textY; stop before the box's bottom padding.
  const textLimit = boxBottom - 2.2 * u - bodySize / 2;
  let textY = boxY + 4 * u;
  ctx.fillStyle = '#1b1712';
  ctx.font = `bold ${bodySize}px ${SANS}`;
  ctx.fillText(`[ ${details.isPublic === false ? 'PRIVATE' : 'PUBLIC'} ]`, textX, textY);
  textY += lineHeight * 1.1;

  ctx.font = `${bodySize}px ${SANS}`;
  const infoLines = [details.dateLabel, details.location]
    .filter(Boolean)
    .flatMap((line) => wrapText((value) => ctx.measureText(value).width, line, textWidth, 2));
  for (const line of infoLines) {
    if (textY > textLimit) break;
    ctx.fillText(line, textX, textY);
    textY += lineHeight;
  }

  ctx.font = `italic ${bodySize}px ${SANS}`;
  const room = Math.floor((textLimit - textY) / lineHeight) + 1;
  if (details.description && room > 0) {
    for (const line of wrapText((value) => ctx.measureText(value).width, details.description, textWidth, room)) {
      ctx.fillText(line, textX, textY);
      textY += lineHeight;
    }
  }

  // Link along the bottom edge.
  ctx.fillStyle = onFrame;
  ctx.font = `bold ${3.8 * u}px ${SANS}`;
  const [link] = wrapText((value) => ctx.measureText(value).width, displayUrl(details.url), innerWidth, 1);
  ctx.fillText(link, x + (width - ctx.measureText(link).width) / 2, y + height - pad + 0.8 * u);

  ctx.textBaseline = 'alphabetic';
  return { height };
}

export function drawShareCard(canvas, details, formatKey = 'story', banner = null, logo = null) {
  const format = SHARE_FORMATS[formatKey] || SHARE_FORMATS.story;
  const { width, height } = format;
  const isStory = formatKey === 'story';
  const ctx = canvas.getContext && canvas.getContext('2d');
  if (!ctx) return false;

  canvas.width = width;
  canvas.height = height;

  // Backdrop: the event background, darkened slightly so the card stands out.
  fillBackground(ctx, details.backgroundColor, 0, 0, width, height);
  const vignette = ctx.createRadialGradient(width / 2, height / 2, width * 0.2, width / 2, height / 2, height * 0.75);
  vignette.addColorStop(0, 'rgba(0,0,0,0)');
  vignette.addColorStop(1, 'rgba(0,0,0,0.35)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, width, height);

  const cardWidth = isStory ? 900 : (height - 120) / CARD_RATIO;
  const cardX = (width - cardWidth) / 2;
  const cardY = isStory ? 150 : 60;
  const { height: cardHeight } = drawCard(ctx, details, cardX, cardY, cardWidth, banner, logo);

  if (isStory) {
    // Big, readable link and a call to action below the card.
    const pillWidth = 860;
    const pillHeight = 104;
    const pillX = (width - pillWidth) / 2;
    const pillY = cardY + cardHeight + 70;
    roundedRect(ctx, pillX, pillY, pillWidth, pillHeight, pillHeight / 4);
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.18)';
    ctx.shadowBlur = 24;
    ctx.shadowOffsetY = 6;
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.restore();
    ctx.fillStyle = '#7b4519';
    ctx.font = `bold 40px ${SANS}`;
    const [linkText] = wrapText((text) => ctx.measureText(text).width, displayUrl(details.url), pillWidth - 80, 1);
    ctx.fillText(linkText, (width - ctx.measureText(linkText).width) / 2, pillY + pillHeight / 2 + 14);

    const onBackground = isLightColor(primaryColor(details.backgroundColor)) ? '#111' : '#fff';
    ctx.fillStyle = onBackground;
    ctx.font = `bold 46px ${SANS}`;
    const cta = 'Join me on Frolik';
    ctx.fillText(cta, (width - ctx.measureText(cta).width) / 2, pillY + pillHeight + 90);
  }

  return true;
}

// Loads an image for drawing. Resolves null if it can't be loaded.
export function loadBanner(src) {
  return new Promise((resolve) => {
    if (!src || typeof Image === 'undefined') {
      resolve(null);
      return;
    }
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });
}
